// packages/shared/src/utils/billingRuleEngine.ts
// Pure client-side billing rule evaluation engine — no side effects.

import type { BillingRule, BillingRuleCategory, PayerCategory } from '../types/models/billingRules';
import type { BillingRuleViolation, BillingRuleEvaluationResult } from '../types/api/billingRules';
import { normalizePlanType, BILLING_RULE_PAYER_CATEGORIES, payerCategoryMatches } from '../constants/insurance';
import { selectCurrentPoc } from './planOfCareStatus';

// ---- Context types ----

export interface RuleEvaluationContext {
  encounter?: {
    clinical_specialty?: string;
    care_setting?: string;
    billing_codes?: {
      cpt_codes?: { code: string; description?: string; units?: number; modifiers?: string[]; minutes?: number; indirect_minutes?: number; timed?: boolean; charge?: number }[];
      icd10_codes?: { code: string; description?: string; is_primary?: boolean }[];
    };
    encounter_date?: string;
    case_id?: number;
    /**
     * False while any CPT row still has a blank required minutes/units cell
     * (see apps/provider/src/utils/cptEntryBlanks.js). Absent = treated as complete.
     */
    cpt_entry_complete?: boolean;
  };
  clinical_specialty?: string;
  care_setting?: string;
  visit_history?: {
    total_visits: number;
    current_visit_number: number;
    last_progress_note_date?: number;
    last_progress_note_visit?: number;
    last_encounter_date?: number;
    poc_certification_date?: number;
    /** Epoch ms of the current POC's certification_end (poc_expiration trigger). */
    poc_certification_end?: number;
    /**
     * True when the org counts only SIGNED progress_note/reassessment notes as
     * milestone notes (settings.documentation.pn_due_signed_only). Enables the
     * overdue branch for `once` recurrence so a milestone stays due past its
     * trigger visit until the qualifying note is signed.
     */
    milestone_requires_signed?: boolean;
  };
  payer?: {
    payer_category?: string;
    organization_payer_id?: string;
  };
}

// ---- Helpers ----

/** Normalizes a timestamp or date string to a YYYY-MM-DD calendar date string for visit deduplication. */
export function toCalendarDate(encounterDate: string | number): string {
  const ts = typeof encounterDate === 'number'
    ? (encounterDate > 1e12 ? encounterDate : encounterDate * 1000)
    : new Date(encounterDate).getTime();
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ---- Filtering ----

const KNOWN_PAYER_CATEGORY_VALUES = new Set(BILLING_RULE_PAYER_CATEGORIES.map(c => c.value));

/** Filter rules that apply to the current context (specialty, payer, active, date range). */
export function filterApplicableRules(rules: BillingRule[], context: RuleEvaluationContext): BillingRule[] {
  const now = Date.now();
  return rules.filter(rule => {
    if (!rule.is_active) return false;
    if (rule.effective_date && rule.effective_date > now) return false;
    if (rule.expiration_date && rule.expiration_date < now) return false;

    // Specialty match
    const contextSpecialty = context.encounter?.clinical_specialty || context.clinical_specialty;
    if (rule.clinical_specialty && contextSpecialty &&
        rule.clinical_specialty !== contextSpecialty) {
      return false;
    }

    // Care setting match
    const contextCareSetting = context.encounter?.care_setting || context.care_setting;
    if (rule.care_setting && contextCareSetting &&
        rule.care_setting !== contextCareSetting) {
      return false;
    }

    // Payer match: specific payer takes precedence over category. Strict:
    // a payer-scoped rule applies ONLY to patients whose insurance carries
    // that stored organization_payer_id (stamped at write time / backfill).
    // Legacy corrupt rows (the old editor wrote category strings into
    // organization_payer_id) are read as category scoping instead.
    const corruptAsCategory = rule.organization_payer_id && KNOWN_PAYER_CATEGORY_VALUES.has(rule.organization_payer_id)
      ? rule.organization_payer_id
      : null;
    if (rule.organization_payer_id && !corruptAsCategory) {
      if (rule.organization_payer_id !== context.payer?.organization_payer_id) {
        return false;
      }
      return true;
    }
    const effectiveCategory = corruptAsCategory || rule.payer_category;
    if (effectiveCategory && effectiveCategory !== 'all' && context.payer?.payer_category) {
      // Billing rules: a Medicare rule also covers Medicare Advantage
      if (!payerCategoryMatches(effectiveCategory, context.payer.payer_category, { medicareIncludesAdvantage: true })) {
        return false;
      }
    }

    return true;
  });
}

// ---- Category evaluators ----

function evaluateNCCIEdit(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
  const codeSet = new Set(cptCodes.map(c => c.code));
  const config = rule.rule_config;
  if (!config?.code_pairs) return null;

  for (const pair of config.code_pairs) {
    if (codeSet.has(pair.code_a) && codeSet.has(pair.code_b)) {
      // Check modifier indicator: if 1, modifier 59/X{EPSU} can resolve it
      if (config.modifier_indicator === 1) {
        const resolverModifiers = ['59', 'XE', 'XP', 'XS', 'XU'];
        const codeB = cptCodes.find(c => c.code === pair.code_b);
        if (codeB?.modifiers?.some(m => resolverModifiers.includes(m))) continue;
      }
      return {
        rule_id: rule.id,
        rule_name: rule.name,
        category: rule.category,
        enforcement_level: rule.enforcement_level,
        message: rule.alert_message || `NCCI edit: ${pair.code_a} and ${pair.code_b} cannot be billed together`,
        resolution_guidance: rule.resolution_guidance,
        affected_codes: [pair.code_a, pair.code_b],
        auto_fix_available: config.modifier_indicator === 1,
        auto_fix_action: config.modifier_indicator === 1 ? `Add modifier 59 to ${pair.code_b}` : undefined,
      };
    }
  }
  return null;
}

function evaluateUnitLimit(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
  const config = rule.rule_config;
  if (!config?.code) return null;

  const match = cptCodes.find(c => c.code === config.code);
  if (!match) return null;

  // 0 / blank units = documented, not billed (same convention as claim generation)
  const units = Number(match.units) || 0;
  const maxUnits = config.max_units_per_encounter;
  if (maxUnits && units > maxUnits) {
    return {
      rule_id: rule.id,
      rule_name: rule.name,
      category: rule.category,
      enforcement_level: rule.enforcement_level,
      message: rule.alert_message || `MUE: ${config.code} has ${units} units (max ${maxUnits})`,
      resolution_guidance: rule.resolution_guidance,
      affected_codes: [config.code],
    };
  }
  return null;
}

function evaluateVisitMilestone(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  const config = rule.rule_config;
  const history = context.visit_history;
  if (!config || !history) return null;

  const currentVisit = history.current_visit_number;
  const triggerVisit = config.trigger_visit_number;
  const hasWindowDays = !!(config.window_days && history.last_progress_note_date);

  const isWithinTimeWindow = hasWindowDays &&
    Math.floor((Date.now() - history.last_progress_note_date) / 86400000) < config.window_days;

  let visitViolation: BillingRuleViolation | null = null;

  if (config.recurrence === 'every_n') {
    const advanceWarning = config.advance_warning_visits || 1;

    for (let offset = advanceWarning; offset >= 1; offset--) {
      if (currentVisit > 0 && (currentVisit + offset) % triggerVisit === 0) {
        const milestoneVisit = currentVisit + offset;
        const previousMilestone = milestoneVisit - triggerVisit;
        if (history.last_progress_note_visit && history.last_progress_note_visit > previousMilestone) {
          break;
        }
        const enforcementOverride = config.advance_warning_enforcement_level || rule.enforcement_level;
        visitViolation = makeVisitViolation(rule, config, currentVisit, milestoneVisit, true, enforcementOverride);
        break;
      }
    }

    if (!visitViolation && currentVisit > 0 && currentVisit % triggerVisit === 0) {
      const previousMilestone = currentVisit - triggerVisit;
      if (!(history.last_progress_note_visit && history.last_progress_note_visit > previousMilestone)) {
        visitViolation = makeVisitViolation(rule, config, currentVisit, currentVisit, false);
      }
    }

    // Overdue: past a milestone without a progress note since the one before it
    if (!visitViolation && currentVisit > 0 && currentVisit % triggerVisit !== 0) {
      const lastMilestone = Math.floor(currentVisit / triggerVisit) * triggerVisit;
      if (lastMilestone > 0) {
        const previousMilestone = lastMilestone - triggerVisit;
        if (!(history.last_progress_note_visit && history.last_progress_note_visit > previousMilestone)) {
          visitViolation = makeVisitViolation(rule, config, currentVisit, lastMilestone, false);
        }
      }
    }
  } else if (config.recurrence === 'once') {
    if (currentVisit === triggerVisit - 1) {
      if (!(history.last_progress_note_visit && history.last_progress_note_visit >= triggerVisit)) {
        visitViolation = makeVisitViolation(rule, config, currentVisit, triggerVisit, true);
      }
    } else if (currentVisit === triggerVisit) {
      if (!(history.last_progress_note_visit && history.last_progress_note_visit >= triggerVisit)) {
        visitViolation = makeVisitViolation(rule, config, currentVisit, currentVisit, false);
      }
    } else if (history.milestone_requires_signed && currentVisit > triggerVisit) {
      // Overdue (signed-only orgs): past the trigger visit with no SIGNED
      // qualifying note at or after it, the milestone stays due. Orgs on the
      // default (any note counts) keep the historical once-and-done behavior.
      if (!(history.last_progress_note_visit && history.last_progress_note_visit >= triggerVisit)) {
        visitViolation = makeVisitViolation(rule, config, currentVisit, triggerVisit, false);
      }
    }
  }

  // When both thresholds are configured, a recent note satisfies the rule
  // even if the visit milestone is hit (and vice versa)
  if (visitViolation && hasWindowDays) {
    if (isWithinTimeWindow) return null;
    return visitViolation;
  }
  if (visitViolation) return visitViolation;

  // Time-based check: fire if days since last progress note >= window_days
  if (hasWindowDays && !isWithinTimeWindow) {
    return {
      rule_id: rule.id,
      rule_name: rule.name,
      category: rule.category,
      enforcement_level: rule.enforcement_level,
      message: rule.alert_message || `${Math.floor((Date.now() - history.last_progress_note_date) / 86400000)} days since last progress note (threshold: ${config.window_days})`,
      resolution_guidance: rule.resolution_guidance,
    };
  }

  return null;
}

function interpolateMessage(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? `{${key}}`));
}

// required_action is stored as an enum key (see RequiredAction in types/models/billingRules);
// only the fallback message below needs a human label for it.
const REQUIRED_ACTION_LABELS: Record<string, string> = {
  progress_note: 'progress note',
  re_evaluation: 're-evaluation',
  poc_renewal: 'plan of care renewal',
  new_evaluation: 'new evaluation',
  authorization_check: 'authorization check',
};

function requiredActionLabel(action: unknown): string | null {
  if (typeof action !== 'string' || !action) return null;
  return REQUIRED_ACTION_LABELS[action] || action;
}

function makeVisitViolation(rule: BillingRule, config: any, currentVisit: number, milestoneVisit: number, isUpcoming: boolean, enforcementOverride?: string): BillingRuleViolation {
  const displayVisit = isUpcoming ? currentVisit + 1 : currentVisit;
  const actionLabel = requiredActionLabel(config.required_action);
  // Custom alert_message describes the due-now case ("required at this visit");
  // applying it to the advance warning would misstate which visit it is about.
  const message = !isUpcoming && rule.alert_message
    ? interpolateMessage(rule.alert_message, { current_visit: displayVisit, trigger_visit: milestoneVisit })
    : isUpcoming
      ? `Next visit (#${milestoneVisit}) requires ${actionLabel || 'action'}`
      : `This is visit #${milestoneVisit} — ${actionLabel || 'action required'}`;

  return {
    rule_id: rule.id,
    rule_name: rule.name,
    category: rule.category,
    enforcement_level: enforcementOverride || rule.enforcement_level,
    message,
    resolution_guidance: rule.resolution_guidance,
    required_action: config.required_action,
  };
}

function evaluateModifierRequirement(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
  const config = rule.rule_config;
  if (!config?.target_codes || !config?.required_modifier) return null;

  for (const cpt of cptCodes) {
    if (config.target_codes.includes(cpt.code)) {
      if (!cpt.modifiers?.includes(config.required_modifier)) {
        return {
          rule_id: rule.id,
          rule_name: rule.name,
          category: rule.category,
          enforcement_level: rule.enforcement_level,
          message: rule.alert_message || `${cpt.code} requires modifier ${config.required_modifier}`,
          resolution_guidance: rule.resolution_guidance,
          affected_codes: [cpt.code],
          auto_fix_available: !!config.auto_append,
          auto_fix_action: config.auto_append ? `Add modifier ${config.required_modifier} to ${cpt.code}` : undefined,
        };
      }
    }
  }
  return null;
}

function evaluateDocumentationTrigger(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  const config = rule.rule_config;
  const history = context.visit_history;
  if (!config || !history) return null;

  if (config.trigger === 'poc_certification' && config.threshold_days) {
    if (history.poc_certification_date) {
      const daysSinceCert = Math.floor((Date.now() - history.poc_certification_date) / 86400000);
      if (daysSinceCert >= config.threshold_days) {
        return {
          rule_id: rule.id,
          rule_name: rule.name,
          category: rule.category,
          enforcement_level: rule.enforcement_level,
          message: rule.alert_message || `POC certification expired (${daysSinceCert} days, threshold: ${config.threshold_days})`,
          resolution_guidance: rule.resolution_guidance,
          required_action: config.required_action || config.required_document,
          trigger: config.trigger,
        };
      }
    }
  }

  if (config.trigger === 'treatment_gap' && config.threshold_days) {
    if (history.last_encounter_date) {
      const daysSinceLastVisit = Math.floor((Date.now() - history.last_encounter_date) / 86400000);
      if (daysSinceLastVisit >= config.threshold_days) {
        return {
          rule_id: rule.id,
          rule_name: rule.name,
          category: rule.category,
          enforcement_level: rule.enforcement_level,
          message: rule.alert_message || `${daysSinceLastVisit}-day gap since last visit — new evaluation required`,
          resolution_guidance: rule.resolution_guidance,
          required_action: config.required_action || config.required_document,
          trigger: config.trigger,
        };
      }
    }
  }

  if (config.trigger === 'poc_expiration') {
    const end = history.poc_certification_end;
    if (end) {
      // End-of-day grace: the POC is still valid through the whole of its
      // certification_end calendar day. daysUntil < 0 means expired.
      const daysUntil = Math.floor((end + 86399999 - Date.now()) / 86400000);
      const expired = daysUntil < 0;
      const daysBefore = Number(config.days_before) || 0;
      if (expired || (daysBefore > 0 && daysUntil <= daysBefore)) {
        const endDate = new Date(end).toLocaleDateString();
        return {
          rule_id: rule.id,
          rule_name: rule.name,
          category: rule.category,
          enforcement_level: rule.enforcement_level,
          message: rule.alert_message || (expired
            ? `Plan of care expired on ${endDate}`
            : `Plan of care expires in ${daysUntil} day(s)`),
          resolution_guidance: rule.resolution_guidance,
          required_action: config.required_action || config.required_document,
          trigger: config.trigger,
          // Distinguishes the actual-expiry firing from the days_before
          // pre-expiry warning; only the former is a PN-due (calendar color /
          // forced note type) signal — see apps/provider utils/pnDue.js.
          expired,
        };
      }
    }
  }

  return null;
}

function evaluateCodeExclusion(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
  const config = rule.rule_config;
  const exclusiveCodes = config?.exclusive_codes || config?.excluded_codes;
  if (!exclusiveCodes) return null;

  const present = exclusiveCodes.filter((code: string) => cptCodes.some(c => c.code === code));
  if (present.length > 1) {
    return {
      rule_id: rule.id,
      rule_name: rule.name,
      category: rule.category,
      enforcement_level: rule.enforcement_level,
      message: rule.alert_message || `Only one of ${present.join(', ')} may be billed per encounter`,
      resolution_guidance: rule.resolution_guidance,
      affected_codes: present,
    };
  }
  return null;
}

function evaluateCodeProhibition(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
  const config = rule.rule_config;
  const prohibitedCodes = config?.prohibited_codes;
  if (!prohibitedCodes) return null;

  const present = prohibitedCodes.filter((code: string) => cptCodes.some(c => c.code === code));
  if (present.length > 0) {
    const reason = config?.reason ? `: ${config.reason}` : '';
    return {
      rule_id: rule.id,
      rule_name: rule.name,
      category: rule.category,
      enforcement_level: rule.enforcement_level,
      message: rule.alert_message || `${present.join(', ')} cannot be billed${reason}`,
      resolution_guidance: rule.resolution_guidance,
      affected_codes: present,
    };
  }
  return null;
}

function evaluateThreshold(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const config = rule.rule_config;
  if (!config?.metric || config?.threshold_value === undefined) return null;

  // Total units across all CPT lines in this encounter. Default direction is a
  // per-session cap (typically payer-scoped via organization_payer_id): exceeding
  // it is the violation; hitting it exactly is allowed. 'at_least' is a minimum:
  // fires when the total is below the value, but only once at least one code is
  // entered AND every row has its minutes/units filled in (cpt_entry_complete),
  // so neither an empty grid nor a half-entered one is flagged. A blank (null)
  // cell suppresses the warning; an explicit 0 is a decision and does not.
  if (config.metric === 'total_units') {
    const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
    const total = cptCodes.reduce((sum, c) => sum + (Number(c.units) || 0), 0);
    if (config.direction === 'at_least') {
      if (cptCodes.length > 0 && context.encounter.cpt_entry_complete !== false && total < config.threshold_value) {
        return {
          rule_id: rule.id,
          rule_name: rule.name,
          category: rule.category,
          enforcement_level: rule.enforcement_level,
          message: rule.alert_message || `Only ${total} unit${total === 1 ? '' : 's'} recorded. This payer expects at least ${config.threshold_value} per visit.`,
          resolution_guidance: rule.resolution_guidance,
          affected_codes: cptCodes.map(c => c.code),
        };
      }
      return null;
    }
    if (total > config.threshold_value) {
      return {
        rule_id: rule.id,
        rule_name: rule.name,
        category: rule.category,
        enforcement_level: rule.enforcement_level,
        message: rule.alert_message || `Total units (${total}) exceed the per-session limit of ${config.threshold_value} for this payer`,
        resolution_guidance: rule.resolution_guidance,
        affected_codes: cptCodes.map(c => c.code),
      };
    }
    return null;
  }

  // Cumulative charges from CPT codes in this encounter
  if (config.metric === 'cumulative_charges') {
    const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
    const total = cptCodes.reduce((sum, c) => sum + (c.charge || 0) * (Number(c.units) || 0), 0);
    if (total >= config.threshold_value) {
      return {
        rule_id: rule.id,
        rule_name: rule.name,
        category: rule.category,
        enforcement_level: rule.enforcement_level,
        message: rule.alert_message || `Threshold exceeded: $${total.toFixed(2)} (limit: $${config.threshold_value})`,
        resolution_guidance: rule.resolution_guidance,
        auto_fix_available: !!config.modifier_to_add,
        auto_fix_action: config.modifier_to_add ? `Add modifier ${config.modifier_to_add}` : undefined,
      };
    }
  }

  return null;
}

function evaluateUnitCalculation(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const config = rule.rule_config;
  if (!config?.method || config.method !== '8_minute_rule') return null;

  const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
  const timeBasedCodes = config.time_based_codes || [];

  // 8-minute rule: total timed minutes must justify total timed units
  let totalMinutes = 0;
  let totalUnits = 0;
  for (const cpt of cptCodes) {
    if (timeBasedCodes.includes(cpt.code)) {
      totalMinutes += cpt.minutes || 0;
      totalUnits += cpt.units || 0;
    }
  }

  if (totalUnits === 0) return null;

  // Under 8-minute rule: need at least 8 mins per unit (with remainder rules)
  // 1 unit = 8-22 min, 2 units = 23-37 min, etc.
  const maxJustifiedUnits = totalMinutes < 8 ? 0 : Math.floor((totalMinutes + 7) / 15);

  if (totalUnits > maxJustifiedUnits) {
    return {
      rule_id: rule.id,
      rule_name: rule.name,
      category: rule.category,
      enforcement_level: rule.enforcement_level,
      message: rule.alert_message || `8-minute rule: ${totalMinutes} minutes supports ${maxJustifiedUnits} units, but ${totalUnits} billed`,
      resolution_guidance: rule.resolution_guidance,
      affected_codes: timeBasedCodes.filter((code: string) => cptCodes.some(c => c.code === code)),
    };
  }
  return null;
}

function deriveChapterFromICD10(code: string): number | null {
  if (!code || code.length < 3) return null;
  const letter = code[0].toUpperCase();
  const num = parseInt(code.substring(1, 3), 10);
  if (isNaN(num)) return null;
  switch (letter) {
    case 'A': case 'B': return 1;
    case 'C': return 2;
    case 'D': return num <= 49 ? 2 : 3;
    case 'E': return 4;
    case 'F': return 5;
    case 'G': return 6;
    case 'H': return num <= 59 ? 7 : 8;
    case 'I': return 9;
    case 'J': return 10;
    case 'K': return 11;
    case 'L': return 12;
    case 'M': return 13;
    case 'N': return 14;
    case 'O': return 15;
    case 'P': return 16;
    case 'Q': return 17;
    case 'R': return 18;
    case 'S': case 'T': return 19;
    case 'V': case 'W': case 'X': case 'Y': return 20;
    case 'Z': return 21;
    case 'U': return 22;
    default: return null;
  }
}

function evaluateDiagnosisRequirement(rule: BillingRule, context: RuleEvaluationContext): BillingRuleViolation | null {
  if (!context.encounter) return null;
  const cptCodes = context.encounter.billing_codes?.cpt_codes || [];
  const icd10Codes = context.encounter.billing_codes?.icd10_codes || [];
  const config = rule.rule_config;
  if (!config?.target_codes || !config?.icd10_match) return null;

  const presentTargets = config.target_codes.filter((tc: string) => cptCodes.some(c => c.code === tc));
  if (presentTargets.length === 0) return null;

  const match = config.icd10_match;
  const hasValidDiagnosis = icd10Codes.some((icd: { code: string }) => {
    const icdCode = icd.code || '';
    switch (match.type) {
      case 'prefix':
        return match.values.some((v: string) => icdCode.startsWith(v));
      case 'exact':
        return match.values.includes(icdCode);
      case 'range':
        return match.values.length >= 2 && icdCode >= match.values[0] && icdCode <= match.values[1] + '\uffff';
      case 'chapter': {
        const chapter = deriveChapterFromICD10(icdCode);
        return chapter !== null && match.values.includes(String(chapter));
      }
      default:
        return false;
    }
  });

  if (!hasValidDiagnosis) {
    return {
      rule_id: rule.id,
      rule_name: rule.name,
      category: rule.category,
      enforcement_level: rule.enforcement_level,
      message: rule.alert_message || `CPT ${presentTargets.join(', ')} requires a supporting diagnosis`,
      resolution_guidance: rule.resolution_guidance || config.valid_diagnosis_description,
      affected_codes: presentTargets,
    };
  }
  return null;
}

// ---- Evaluator dispatch ----

const EVALUATORS: Record<BillingRuleCategory, (rule: BillingRule, ctx: RuleEvaluationContext) => BillingRuleViolation | null> = {
  ncci_edit: evaluateNCCIEdit,
  unit_limit: evaluateUnitLimit,
  visit_milestone: evaluateVisitMilestone,
  modifier_requirement: evaluateModifierRequirement,
  documentation_trigger: evaluateDocumentationTrigger,
  code_exclusion: evaluateCodeExclusion,
  code_prohibition: evaluateCodeProhibition,
  threshold: evaluateThreshold,
  unit_calculation: evaluateUnitCalculation,
  diagnosis_requirement: evaluateDiagnosisRequirement,
};

// ---- Context builder ----

/**
 * Builds a RuleEvaluationContext from case data. Pure function — no hooks or side effects.
 * Mirrors the context-building logic in useBillingRuleEvaluation but callable outside React.
 */
export function buildCaseEvaluationContext(options: {
  caseObj: { clinical_specialty?: string; care_setting?: string; id?: number };
  encounters: Array<{ id?: number; status?: string; encounter_date?: string; encounter_type?: string; documentation_status?: string; counts_toward_visit_total?: boolean }>;
  clinicalNotes?: Array<{ id?: string; encounter_id?: string; note_type?: string; signed?: boolean }>;
  plansOfCare?: Array<{ case_id?: number; status?: string; certification_start?: number; certification_end?: number }>;
  patient?: { insurance_primary?: any; insurance?: any[] };
  /**
   * Org setting settings.documentation.pn_due_signed_only: only SIGNED
   * progress_note/reassessment notes qualify as the milestone note. The
   * note-mode decision (does the case have ANY typed note) is unaffected, so a
   * case whose only progress note is a draft stays in note mode rather than
   * falling back to the legacy follow_up/documentation_status heuristic.
   */
  milestoneRequiresSigned?: boolean;
}): RuleEvaluationContext {
  const { caseObj, encounters, clinicalNotes, plansOfCare, patient, milestoneRequiresSigned } = options;

  // Non-counting encounters (counts_toward_visit_total === false, e.g. quick
  // discharges/case notes) are excluded from milestone math entirely — including
  // the last-progress-note scan. Known edge case: a counting and non-counting
  // encounter on the same calendar date used to merge into one visit via the
  // date dedup; with the filter, only the counting one contributes.
  const activeVisits = encounters.filter(e => e.status !== 'cancelled' && e.counts_toward_visit_total !== false);
  const sorted = [...activeVisits].sort((a, b) =>
    new Date(a.encounter_date as string).getTime() - new Date(b.encounter_date as string).getTime()
  );

  // Deduplicate: group by calendar date, count unique dates as visits
  const uniqueVisitDates = [...new Set(
    sorted.map(e => toCalendarDate(e.encounter_date as string))
  )];
  const currentVisitNum = uniqueVisitDates.length;

  // Build encounter → note_type lookup from clinical notes. Note mode is
  // decided on ANY typed note (hasNoteData); the milestone map itself only
  // admits signed notes when the org requires it.
  const MILESTONE_NOTE_TYPES = ['progress_note', 'reassessment'];
  const noteTypeByEncounterId: Record<string, string> = {};
  let hasNoteData = false;
  if (clinicalNotes && clinicalNotes.length > 0) {
    for (const note of clinicalNotes) {
      if (note.encounter_id && note.note_type) {
        hasNoteData = true;
        if (milestoneRequiresSigned && note.signed !== true) continue;
        const key = String(note.encounter_id);
        const existing = noteTypeByEncounterId[key];
        if (!existing || !MILESTONE_NOTE_TYPES.includes(existing)) {
          noteTypeByEncounterId[key] = note.note_type;
        }
      }
    }
  }

  let lastProgressNoteDate: number | null = null;
  let lastProgressNoteVisit: number | null = null;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const enc = sorted[i];
    if (hasNoteData) {
      const noteType = noteTypeByEncounterId[String(enc.id)];
      if (noteType === 'progress_note' || noteType === 'reassessment') {
        lastProgressNoteDate = new Date(enc.encounter_date as string).getTime();
        lastProgressNoteVisit = uniqueVisitDates.indexOf(toCalendarDate(enc.encounter_date as string)) + 1;
        break;
      }
    } else {
      if (enc.encounter_type === 'follow_up' || enc.documentation_status === 'signed') {
        lastProgressNoteDate = new Date(enc.encounter_date as string).getTime();
        lastProgressNoteVisit = uniqueVisitDates.indexOf(toCalendarDate(enc.encounter_date as string)) + 1;
        break;
      }
    }
  }

  // Fallback: if no progress note found, use first encounter date so the
  // 30-day window_days check doesn't skip entirely on new cases.
  // Only set lastProgressNoteDate — leave lastProgressNoteVisit null so
  // visit milestone checks correctly detect "no progress note done."
  if (lastProgressNoteDate === null && sorted.length > 0) {
    lastProgressNoteDate = new Date(sorted[0].encounter_date as string).getTime();
  }

  const lastEncounterDate = sorted.length > 0
    ? new Date(sorted[sorted.length - 1].encounter_date as string).getTime()
    : null;

  const pocCertificationDate = derivePocCertificationDate(plansOfCare, caseObj.id);
  const pocCertificationEnd = derivePocCertificationEnd(plansOfCare, caseObj.id);
  const payerInfo = derivePayerContext(patient);

  return {
    clinical_specialty: caseObj.clinical_specialty,
    care_setting: caseObj.care_setting,
    visit_history: {
      total_visits: uniqueVisitDates.length,
      current_visit_number: currentVisitNum,
      last_progress_note_date: lastProgressNoteDate ?? undefined,
      last_progress_note_visit: lastProgressNoteVisit ?? undefined,
      last_encounter_date: lastEncounterDate ?? undefined,
      poc_certification_date: pocCertificationDate ?? undefined,
      poc_certification_end: pocCertificationEnd ?? undefined,
      milestone_requires_signed: milestoneRequiresSigned || undefined,
    },
    payer: payerInfo,
  };
}

/**
 * Latest POC certification date (epoch ms) among active-ish plans for a case.
 * Extracted so callers that receive PRECOMPUTED visit-history aggregates (the
 * calendar-billing-context v2 path) can derive this one scalar from raw POC
 * rows without the full buildCaseEvaluationContext reduction.
 */
export function derivePocCertificationDate(
  plansOfCare: Array<{ case_id?: number; status?: string; certification_start?: number; created_at?: number }> | undefined,
  caseId?: number
): number | null {
  const scoped = (plansOfCare || []).filter(p => !caseId || p.case_id === caseId);
  const current = selectCurrentPoc(scoped);
  const certStart = current?.certification_start;
  return certStart ? (certStart > 1e12 ? certStart : certStart * 1000) : null;
}

/**
 * Certification END date (epoch ms) of the current POC for a case. Mirror of
 * derivePocCertificationDate for the poc_expiration documentation trigger.
 */
export function derivePocCertificationEnd(
  plansOfCare: Array<{ case_id?: number; status?: string; certification_start?: number; certification_end?: number; created_at?: number }> | undefined,
  caseId?: number
): number | null {
  const scoped = (plansOfCare || []).filter(p => !caseId || p.case_id === caseId);
  const current = selectCurrentPoc(scoped);
  const certEnd = current?.certification_end;
  return certEnd ? (certEnd > 1e12 ? certEnd : certEnd * 1000) : null;
}

/**
 * Payer sub-context from a patient's primary insurance. Extracted for the same
 * precomputed-aggregate path as derivePocCertificationDate.
 */
export function derivePayerContext(
  patient?: { insurance_primary?: any; insurance?: any[] }
): { payer_category?: string; organization_payer_id?: string } | undefined {
  const insuranceList = (patient as any)?.insurance;
  const primaryInsurance = (patient as any)?.insurance_primary
    || (Array.isArray(insuranceList)
      ? insuranceList.find((i: any) => i?.is_primary) || insuranceList[0]
      : undefined);
  return primaryInsurance ? {
    payer_category: normalizePlanType(primaryInsurance.plan_type),
    organization_payer_id: primaryInsurance.organization_payer_id,
  } : undefined;
}

// ---- Main entry point ----

/**
 * Evaluates billing rules against the given context and returns violations.
 * Pure function — no side effects, no API calls.
 */
export function evaluateBillingRules(
  rules: BillingRule[],
  context: RuleEvaluationContext,
  categoryFilter?: BillingRuleCategory[]
): BillingRuleEvaluationResult {
  const applicable = filterApplicableRules(rules, context);
  const filtered = categoryFilter
    ? applicable.filter(r => categoryFilter.includes(r.category))
    : applicable;

  const violations: BillingRuleViolation[] = [];

  for (const rule of filtered) {
    const evaluator = EVALUATORS[rule.category];
    if (!evaluator) continue;
    const violation = evaluator(rule, context);
    if (violation) violations.push(violation);
  }

  return {
    violations,
    has_hard_blocks: violations.some(v => v.enforcement_level === 'hard_block'),
    has_acknowledge_required: violations.some(v => v.enforcement_level === 'acknowledge_required'),
    has_soft_warnings: violations.some(v => v.enforcement_level === 'soft_warning'),
    evaluated_rule_count: filtered.length,
  };
}
